/**
 * Creatable tag combobox — "Select a category or create a new one". Pick an
 * existing tag from the filtered list, or type a name and press Enter to add it
 * (a name not in the catalog is created server-side on save). Assigned tags show
 * as removable chips. Pure controlled component: every change calls `onChange`
 * with the next full list; the parent persists it.
 */
import { useMemo, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Badge, Input } from '@vnetwork/ui/primitives';
import type { WafTag } from '@/api/waf';
import styles from './TagEditor.module.css';

export function TagEditor({
  value,
  options,
  busy,
  onChange,
}: {
  value: string[];
  options: WafTag[];
  busy?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState('');

  const add = (raw: string) => {
    const name = raw.trim();
    if (!name || value.includes(name)) {
      setQuery('');
      return;
    }
    onChange([...value, name]);
    setQuery('');
  };
  const remove = (name: string) => onChange(value.filter((v) => v !== name));

  const q = query.trim().toLowerCase();
  const suggestions = useMemo(
    () =>
      options
        .map((o) => o.name)
        .filter((n) => !value.includes(n) && (!q || n.toLowerCase().includes(q))),
    [options, value, q],
  );
  // Whether the typed text already exists (as an option or an assigned chip).
  const exists =
    !!q &&
    (options.some((o) => o.name.toLowerCase() === q) ||
      value.some((v) => v.toLowerCase() === q));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim()) add(query);
    } else if (e.key === 'Backspace' && !query && value.length) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)', minWidth: 240 }}>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {value.map((name) => (
            <Badge key={name} tone="info" appearance="subtle" size="sm">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                {name}
                <button
                  type="button"
                  className={styles.remove}
                  disabled={busy}
                  aria-label={`Remove ${name}`}
                  onClick={() => remove(name)}
                >
                  <X size={11} />
                </button>
              </span>
            </Badge>
          ))}
        </div>
      )}

      <Input
        value={query}
        disabled={busy}
        placeholder="Select a category or create a new one"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
      />

      {(suggestions.length > 0 || (!!q && !exists)) && (
        <div className={styles.list}>
          {suggestions.map((name) => (
            <button
              key={name}
              type="button"
              className={styles.option}
              disabled={busy}
              onClick={() => add(name)}
            >
              {name}
            </button>
          ))}
          {!!q && !exists && (
            <button
              type="button"
              className={`${styles.option} ${styles.create}`}
              disabled={busy}
              onClick={() => add(query)}
            >
              Create &ldquo;{query.trim()}&rdquo;
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TagEditor;
